/**
 * Address List Component
 *
 * Container component for managing multiple addresses
 *
 * Uses optimized backend endpoints:
 * - GET /api/app/contacts/{contactId}/addresses - Returns joined address data
 * - No more client-side joins needed!
 */

import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, switchMap } from 'rxjs';
import { AddressService } from '../../core/services/address.service';
import { ContactAddressService } from '../../core/services/contact-address.service';
import { CountryService } from '../../core/services/country.service';
import { CategoryDefinitionService } from '../../core/services/category-definition.service';
import { UserRoleService } from '../../services/user-role.service';
import { APP_SCHEMA_ENDPOINTS } from '../../core/api';
import type {
  ContactAddressWithDetails,
  AddressCreate,
  AddressUpdate,
  ContactAddressCreate,
  ContactAddressUpdate,
} from '../../core/models';
import { AddressCardComponent } from './address-card.component';
import {
  AddressEditDialogComponent,
  AddressEditDialogData,
  AddressEditDialogResult,
} from '../dialogs/address-edit-dialog.component';
import { TranslatePipe, I18nStore } from '../../core/features/translations';

@Component({
  selector: 'app-address-list',
  imports: [CommonModule, AddressCardComponent, TranslatePipe],
  template: `
    <div class="w-full font-sans">
      <div class="flex flex-col gap-3 mb-6 pb-4 border-b-2 border-gray-200 md:flex-row md:justify-between md:items-center">
        <h2 class="m-0 text-gray-900 text-2xl font-semibold">{{ 'portal.mydata.addresses.title' | translate }}</h2>
        @if (!roleService.isLecturer()) {
          <button type="button" 
                  class="flex items-center justify-center gap-2 py-2.5 px-5 bg-blue-600 text-white border-0 rounded font-medium text-[0.95rem] cursor-pointer transition-colors duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 w-full md:w-auto" 
                  (click)="onAddAddress()">
            <span class="text-[1.2rem]">➕</span>
            <span>{{ 'portal.mydata.addresses.add' | translate }}</span>
          </button>
        }
      </div>

      <!-- Loading State -->
      @if (loading()) {
        <div class="flex flex-col items-center justify-center p-12 gap-4">
          <div class="w-12 h-12 border-4 border-gray-100 border-t-blue-600 rounded-full animate-spin"></div>
          <p class="text-gray-600 text-base">{{ 'portal.mydata.addresses.loading' | translate }}</p>
        </div>
      }

      <!-- Error State -->
      @if (error()) {
        <div class="flex flex-col items-center gap-4 p-8 bg-red-50 border border-red-500 rounded-lg">
          <div class="text-[3rem]">⚠️</div>
          <p class="text-red-700 text-base text-center">{{ error() }}</p>
        </div>
      }

      <!-- Address Cards -->
      @if (!loading() && !error()) {
        <div class="w-full">
          @if (addresses().length === 0) {
            <div class="flex flex-col items-center gap-4 p-12 bg-gray-50 rounded-lg text-center">
              <p class="text-gray-600 text-[1.1rem] m-0">{{ 'portal.mydata.addresses.empty' | translate }}</p>
              @if (!roleService.isLecturer()) {
                <button type="button" 
                        class="py-2.5 px-5 bg-blue-600 text-white border-0 rounded font-medium text-[0.95rem] cursor-pointer transition-colors duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" 
                        (click)="onAddAddress()">
                  {{ 'portal.mydata.addresses.addfirst' | translate }}
                </button>
              }
            </div>
          } @else {
            <div class="flex flex-col gap-4 w-full">
              @for (address of sortedAddresses(); track address.contact_address_id) {
                <app-address-card
                  [address]="address"
                  [disablePrimaryEdit]="roleService.isLecturer()"
                  (edit)="onEditAddress($event)"
                  (delete)="onDeleteAddress($event)"
                />
              }
            </div>
          }
        </div>
      }

      <!-- Delete Confirmation Modal -->
      @if (deletingAddress()) {
        <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]" (click)="onCancelDelete()">
          <div class="bg-white rounded-lg p-6 max-w-[500px] w-[90%] shadow-xl font-sans" (click)="$event.stopPropagation()">
            <h3 class="m-0 mb-4 text-gray-900 text-xl font-semibold">{{ 'portal.mydata.addresses.delete.title' | translate }}</h3>
            <p class="m-0 mb-6 text-gray-600 text-base leading-relaxed">
              {{ 'portal.mydata.addresses.delete.confirm' | translate }}
            </p>
            <div class="flex justify-end gap-3">
              <button
                type="button"
                class="flex items-center gap-2 py-2.5 px-5 bg-white text-gray-600 border border-gray-300 rounded font-medium text-[0.95rem] cursor-pointer transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                (click)="onCancelDelete()"
                [disabled]="deleting()"
              >
                {{ 'portal.mydata.addresses.delete.cancel' | translate }}
              </button>
              <button
                type="button"
                class="flex items-center gap-2 py-2.5 px-5 bg-red-600 text-white border-0 rounded font-medium text-[0.95rem] cursor-pointer transition-all duration-200 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                (click)="onConfirmDelete()"
                [disabled]="deleting()"
              >
                @if (deleting()) {
                  <span class="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  <span>{{ 'portal.mydata.addresses.delete.deleting' | translate }}</span>
                } @else {
                  <span>{{ 'portal.mydata.addresses.delete.delete' | translate }}</span>
                }
              </button>
            </div>
          </div>
        </div>
      }
    </div>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressListComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly dialog = inject(MatDialog);
  private readonly addressService = inject(AddressService);
  private readonly contactAddressService = inject(ContactAddressService);
  private readonly countryService = inject(CountryService);
  private readonly categoryService = inject(CategoryDefinitionService);
  private readonly i18n = inject(I18nStore);
  protected readonly roleService = inject(UserRoleService);

  // Inputs
  readonly contactId = input.required<number>();
  // ✅ Optional initial addresses from parent (prevents redundant API call)
  readonly initialAddresses = input<ContactAddressWithDetails[]>([]);

  // Signals
  readonly addresses = signal<ContactAddressWithDetails[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly deletingAddress = signal<ContactAddressWithDetails | null>(null);
  readonly deleting = signal(false);

  // Computed
  readonly sortedAddresses = computed(() => {
    const addrs = this.addresses();
    // Sort: primary first, then by category
    return [...addrs].sort((a, b) => {
      if (a.is_primary_address && !b.is_primary_address) return -1;
      if (!a.is_primary_address && b.is_primary_address) return 1;
      return (a.fk_category_id || 0) - (b.fk_category_id || 0);
    });
  });

  ngOnInit(): void {
    // ✅ REMOVED: loadCountries() - Countries are loaded once in parent component (my-data.ts)
    this.loadCategories();

    // ✅ Use initial addresses from parent if available (prevents redundant API call)
    const initial = this.initialAddresses();
    if (initial && initial.length > 0) {
      this.addresses.set(initial);
    } else {
      console.log('🔄 No initial addresses provided, loading from API');
      this.loadAddresses();
    }
  }

  private loadCategories(): void {
    this.categoryService.getSideAddressCategories().subscribe();
  }

  /**
   * Load addresses using new backend endpoint with server-side joins
   * No more client-side joining needed!
   */
  private loadAddresses(): void {
    this.loading.set(true);
    this.error.set(null);

    const url = APP_SCHEMA_ENDPOINTS.contacts.addresses(this.contactId());

    this.http
      .get<ContactAddressWithDetails[]>(url, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      })
      .subscribe({
        next: (addresses) => {
          this.addresses.set(addresses);
          this.loading.set(false);
        },
        error: (err) => {
          console.error('❌ Error loading addresses:', err);
          this.error.set(this.i18n.t('portal.mydata.addresses.error.loading'));
          this.loading.set(false);
        },
      });
  }

  onAddAddress(): void {
    const dialogData: AddressEditDialogData = {
      address: null,
      contactId: this.contactId(),
    };

    const dialogRef = this.dialog.open(AddressEditDialogComponent, {
      width: '1200px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      panelClass: 'large-dialog',
      data: dialogData,
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((result: AddressEditDialogResult | undefined) => {
      if (result) {
        this.createAddress(result.formData);
      }
    });
  }

  onEditAddress(address: ContactAddressWithDetails): void {
    const dialogData: AddressEditDialogData = {
      address: address,
      contactId: this.contactId(),
    };

    const dialogRef = this.dialog.open(AddressEditDialogComponent, {
      width: '1200px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      panelClass: 'large-dialog',
      data: dialogData,
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((result: AddressEditDialogResult | undefined) => {
      if (result) {
        this.updateAddress(address, result.formData);
      }
    });
  }

  onDeleteAddress(address: ContactAddressWithDetails): void {
    if (address.is_primary_address) {
      alert(this.i18n.t('portal.mydata.addresses.error.cannotDeletePrimary'));
      return;
    }
    this.deletingAddress.set(address);
  }

  onCancelDelete(): void {
    this.deletingAddress.set(null);
  }

  /**
   * Get category name by ID from loaded categories
   */
  private getCategoryNameById(categoryId: number): string {
    const category = this.categoryService
      .sideAddressCategories()
      .find((c) => c.category_id === categoryId);
    return category?.description || '';
  }

  /**
   * Check if category is an invoice/billing address
   * Matches: Rechnungsadresse, Rechnung, Invoice, Invoicing
   */
  private isInvoiceCategory(categoryName: string): boolean {
    const normalized = categoryName.toLowerCase().trim();
    return (
      normalized.includes('rechnung') ||
      normalized.includes('invoice') ||
      normalized.includes('invoicing') ||
      normalized === 'rechnungsadresse'
    );
  }

  /**
   * Check if category is an IV/advisor address
   * Matches: IV/Berater, IV, Berater
   */
  private isIvCategory(categoryName: string): boolean {
    const normalized = categoryName.toLowerCase().trim();
    return normalized.includes('iv') || normalized.includes('berater');
  }

  /**
   * Check if category is a parents address
   * Matches: Eltern, Parents
   */
  private isParentsCategory(categoryName: string): boolean {
    const normalized = categoryName.toLowerCase().trim();
    return normalized.includes('eltern') || normalized.includes('parents');
  }

  onConfirmDelete(): void {
    const address = this.deletingAddress();
    if (!address) return;

    this.deleting.set(true);

    // Delete contact_address entry (keeps the address record)
    this.contactAddressService.deleteContactAddress(address.contact_address_id).subscribe({
      next: () => {
        this.deleting.set(false);
        this.deletingAddress.set(null);
        this.loadAddresses();
      },
      error: (err) => {
        console.error('❌ Error deleting address:', err);
        alert(this.i18n.t('portal.mydata.addresses.error.deleting'));
        this.deleting.set(false);
      },
    });
  }

  private createAddress(formData: any): void {
    // Step 1: Create address record
    // Convert fk_country_id from string to number (HTML select returns strings)
    const addressData: AddressCreate = {
      companyname: formData.companyname || undefined,
      salutation: formData.salutation || undefined,
      prename: formData.prename || undefined,
      lastname: formData.lastname || undefined,
      street: formData.street || undefined,
      zip: formData.zip || undefined,
      town: formData.town || undefined,
      region: formData.region || undefined,
      fk_country_id: formData.fk_country_id ? Number(formData.fk_country_id) : undefined,
    };

    this.addressService
      .createAddress(addressData)
      .pipe(
        switchMap((createdAddress) => {
          // Step 2: Create contact_address link
          // IMPORTANT: Convert all IDs to numbers (HTML forms return strings)
          const categoryId = Number(formData.fk_category_id);
          const categoryName = this.getCategoryNameById(categoryId);

          const contactAddressData: ContactAddressCreate = {
            fk_contact_id: Number(this.contactId()),
            fk_address_id: Number(createdAddress.address_id),
            fk_category_id: categoryId,
            is_primary_address: false,
            is_invoice_address: this.isInvoiceCategory(categoryName),
            is_iv_address: this.isIvCategory(categoryName),
            is_parents_address: this.isParentsCategory(categoryName),
          };
          return this.contactAddressService.createContactAddress(contactAddressData);
        }),
      )
      .subscribe({
        next: () => {
          this.loadAddresses();
        },
        error: (err) => {
          console.error('❌ Error creating address:', err);
          alert(this.i18n.t('portal.mydata.addresses.error.creating'));
        },
      });
  }

  private updateAddress(address: ContactAddressWithDetails, formData: any): void {
    const addressUpdate: AddressUpdate = {
      companyname: formData.companyname,
      salutation: formData.salutation,
      prename: formData.prename,
      lastname: formData.lastname,
      street: formData.street,
      zip: formData.zip,
      town: formData.town,
      region: formData.region,
      fk_country_id: formData.fk_country_id ? Number(formData.fk_country_id) : undefined,
    };

    // Map category_id to appropriate boolean flags based on category name
    const categoryId = Number(formData.fk_category_id);
    const categoryName = this.getCategoryNameById(categoryId);

    const contactAddressUpdate: ContactAddressUpdate = {
      fk_category_id: categoryId,
      is_invoice_address: this.isInvoiceCategory(categoryName),
      is_iv_address: this.isIvCategory(categoryName),
      is_parents_address: this.isParentsCategory(categoryName),
    };

    forkJoin({
      address: this.addressService.updateAddress(address.fk_address_id, addressUpdate),
      contactAddress: this.contactAddressService.updateContactAddress(
        address.contact_address_id,
        contactAddressUpdate,
      ),
    }).subscribe({
      next: () => {
        this.loadAddresses();
      },
      error: (err) => {
        console.error('❌ Error updating address:', err);
        alert(this.i18n.t('portal.mydata.addresses.error.updating'));
      },
    });
  }
}
